import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Condition, ICondition, IControlStyle, IDataSource, IDependencyMap, ISelectOption, IToken } from "../../services/form-builder/form.interface";
import { ControlType } from "../enums/ControlType";
import { EventEmitter } from "@angular/core";
import { Subject } from "rxjs";

export abstract class BaseControl<T extends ControlType> implements IControlStyle {
  private _name: string;
  nameChanged: EventEmitter<string> = new EventEmitter<string>();
  label: string;
  _type: T;
  value?: string;
  private _isRequired?: boolean;
  private _startOnNewLine?: boolean;
  private _widthSmallScreen?: string;
  private _widthMediumScreen?: string;
  private _widthLargeScreen?: string;
  private _widthLargerScreen?: string;
  private _enableIf?: IToken[];
  private _visibleIf?: IToken[];
  private _disable?: boolean = false;
  private _visible?: boolean = true;

  protected _formControl: FormControl;
  protected _conditionList: ICondition[] = [];

  //This event is called when the clear current page button is clicked.
  //This should only be called by custom controls that have complex internal states that needs to be reset.
  resetEvent: Subject<void> = new Subject<void>();

  constructor(data: any) {
    if (!data.name)
      throw new Error('Name cannot be null or empty');
    if (!data.label)
      throw new Error('Label cannot be null or empty');

    this._type = data.type;
    this._name = data.name;
    this.label = data.label;
    this.isRequired = data.isRequired;
    this._formControl = data.FormControl ?? new FormControl(data.value);
    this.startOnNewLine = data.startOnNewLine;
    this.widthSmallScreen = data.widthSmallScreen;
    this.widthMediumScreen = data.widthMediumScreen;
    this.widthLargeScreen = data.widthLargeScreen;
    this.widthLargerScreen = data.widthLargerScreen;
    this.enableIf = data.enableIf;
    this.visibleIf = data.visibleIf;
    this.disable = data.disable;
    this.setValidators();
  }

  //#region Getters and Setters

  // Override in derived classes to provide specific condition lists
  get conditionList(): ICondition[] {
    return [
      { label: 'Empty', value: Condition.Empty },
      { label: 'Not Empty', value: Condition.NotEmpty },
      { label: 'Equals', value: Condition.Equals },
      { label: 'Does Not Equal', value: Condition.DoesNotEqual },
      { label: 'Greater Than', value: Condition.GreaterThan },
      { label: 'Less Than', value: Condition.LessThan },
    ];
  }

  get name(): string {
    return this._name;
  }

  set name(newName: string) {
    if (this._name !== newName) {
      this._name = newName;
      this.nameChanged.emit(newName);
    }
  }

  get type(): T {
    return this._type;
  }

  get FormControl(): FormControl | FormGroup {
    return this._formControl;
  }

  get isRequired(): boolean {
    return this._isRequired ?? false;
  }

  set isRequired(value: boolean) {
    this._isRequired = (value === false) ? undefined : value;
  }

  get startOnNewLine(): boolean {
    return this._startOnNewLine ?? false;
  }

  set startOnNewLine(value: boolean) {
    this._startOnNewLine = (value === false) ? undefined : value;
  }

  get widthSmallScreen(): string {
    return this._widthSmallScreen ?? 'w-12';
  }

  set widthSmallScreen(value: string) {
    this._widthSmallScreen = (value === 'w-12' || value === '') ? undefined : value;
  }

  get widthMediumScreen(): string {
    return this._widthMediumScreen ?? 'w-12';
  }

  set widthMediumScreen(value: string) {
    this._widthMediumScreen = (value === 'w-12' || value === '') ? undefined : value;
  }

  get widthLargeScreen(): string {
    return this._widthLargeScreen ?? 'w-12';
  }

  set widthLargeScreen(value: string) {
    this._widthLargeScreen = (value === 'w-12' || value === '') ? undefined : value;
  }

  get widthLargerScreen(): string {
    return this._widthLargerScreen ?? 'w-12';
  }

  set widthLargerScreen(value: string) {
    this._widthLargerScreen = (value === 'w-12' || value === '') ? undefined : value;
  }

  get enableIf(): IToken[] {
    return this._enableIf ?? [];
  }

  set enableIf(value: IToken[]) {
    this._enableIf = value?.length === 0 ? undefined : value;
  }

  get visibleIf(): IToken[] {
    return this._visibleIf ?? [];
  }

  set visibleIf(value: IToken[]) {
    this._visibleIf = value?.length === 0 ? undefined : value;
  }

  get disable(): boolean {
    return this._disable ?? false;
  }

  set disable(value: boolean) {
    this._disable = value ?? false;
    if (value) {
      this._formControl.disable();
    } else {
      this._formControl.enable();
    }
  }

  get visible(): boolean {
    return this._visible ?? true;
  }
  
  set visible(value: boolean) {
    this._visible = value ?? true;
  }

  //#endregion Getters and Setters

  hasConditions(): boolean {
    return (this.enableIf !== undefined && this.enableIf.length > 0) ||
      (this.visibleIf !== undefined && this.visibleIf.length > 0);
  }

  setValidators() {
    this._formControl.clearValidators();
    if (this.isRequired) {
      this._formControl.setValidators([Validators.required]);
    }    
  }

  emitResetEvent(){
    this.resetEvent.next();
  }

  toJSON(): any {
    return {
      type: this.type,
      name: this._name,
      label: this.label,
      isRequired: this._isRequired,
      widthSmallScreen: this._widthSmallScreen,
      widthMediumScreen: this._widthMediumScreen,
      widthLargeScreen: this._widthLargeScreen,
      widthLargerScreen: this._widthLargerScreen,
      startOnNewLine: this._startOnNewLine,
      enableIf: this._enableIf?.map(({ valueChangedsubId, nameChangedSubId, ...obj }) => obj),
      visibleIf: this._visibleIf?.map(({ valueChangedsubId, nameChangedSubId, ...obj }) => obj)
    }
  }
}

export abstract class Options<T extends ControlType> extends BaseControl<T> {
  private _options: ISelectOption[];
  private _displayedOptions: ISelectOption[];
  private _dependencyMap?: Set<IDependencyMap>;
  private _dataSource: IDataSource = IDataSource.Interno;
  private _loadExternalDatasource: boolean = false;

  constructor(data: any) {
    super(data);
    //we are using JSONB, this doesnt keep track of the order of the keys, so we order the value and text keys
    this._options = data.options?.map((option: ISelectOption) => {
      const { value, text, ...rest } = option;
      return { value, text, ...rest };
    }) ?? [{ value: '1', text: 'Option1' }, { value: '2', text: 'Option2' }];

    this._displayedOptions = this._options;
    this.dependencyMap = new Set(data.dependencyMap);
    this.dataSource = data.dataSource;
  }

  //#region Getters and Setters
  get options(): ISelectOption[] {
    return this._options;
  }

  set options(value: ISelectOption[]) {
    this._options = value;
    this._displayedOptions = value;
    this.FormControl.setValue(null);
  }

  get displayedOptions(): ISelectOption[] {
    return this._displayedOptions;
  }

  get dependencyMap(): Set<IDependencyMap> {
    return this._dependencyMap ?? new Set();
  }

  set dependencyMap(value: Set<IDependencyMap>) {
    this._dependencyMap = (value?.size === 0) ? undefined : value;
  }

  get dataSource(): IDataSource {
    return this._dataSource ?? IDataSource.Interno;
  }

  set dataSource(value: IDataSource) {
    this._dataSource = value;
  }

  get loadExternalDatasource(): boolean {
    return this._loadExternalDatasource;
  }

  set loadExternalDatasource(value: boolean) {
    this._loadExternalDatasource = value;
  }
  //#endregion Getters and Setters

  getOptionKeys() {
    return Object.keys(this._options[0]) ?? [];
  }

  public resetFilter() {
    this._displayedOptions = this._options;
  }

  public filterOptions(mappedField: string, filterValue: string): void {
    this._displayedOptions = this._displayedOptions.filter(option => {
      const mappedValue = option[mappedField]?.toString();
      return mappedValue === filterValue.toString();
    });
  }

  private getOptionsJson() {
    return this.dataSource === IDataSource.Interno ? this._options : [];
  }

  override toJSON(): any {
    return {
      ...super.toJSON(),
      options: this.getOptionsJson(),
      dependencyMap: this._dependencyMap === undefined ? undefined : Array.from(this._dependencyMap).map(({ filterChangedSubId, nameChangedSubId, ...obj }) => obj),
      dataSource: this._dataSource
    }
  }
}
